import { Button } from "@/components/ui/button";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  TelemetrySpan,
  TelemetrySpanStatus,
} from "../../pavilion/schemas";
import { parseCustomAttributes } from "../lib/telemetry-utils";
import { TelemetryExpandedContent } from "./telemetry/TelemetryExpandedContent";
import { TelemetryFilters } from "./telemetry/TelemetryFilters";

interface ExpandedRows {
  [key: string]: boolean;
}

const statusColors = {
  ok: "text-green-700",
  error: "text-destructive",
  cancelled: "text-yellow-700",
} as const;

const createColumns = (): ColumnDef<TelemetrySpan>[] => [
  {
    accessorKey: "startTime",
    header: "Timestamp",
    cell: ({ row }) => {
      const date = new Date(row.getValue("startTime"));
      return (
        <div className="text-xs font-mono text-left">
          <div>
            {date.toLocaleTimeString("en-US", {
              timeZone: "UTC",
              hour12: false,
            })}
          </div>
          <div className="text-muted-foreground">
            {date.toLocaleDateString("en-US", { timeZone: "UTC" })}
          </div>
        </div>
      );
    },
    size: 140,
    enableResizing: true,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as keyof typeof statusColors;
      return (
        <div
          className={`text-xs font-medium capitalize ${statusColors[status]}`}
        >
          {status}
        </div>
      );
    },
    size: 80,
    enableResizing: true,
  },
  {
    accessorKey: "operationName",
    header: "Operation",
    cell: ({ row }) => {
      const operation = row.getValue("operationName") as string;
      return (
        <div className="text-xs font-mono text-muted-foreground">
          {operation}
        </div>
      );
    },
    size: 160,
    enableResizing: true,
  },
  {
    accessorKey: "spanId",
    header: "Span ID",
    cell: ({ row }) => (
      <div
        className="text-xs font-mono text-muted-foreground"
        title={row.getValue("spanId") as string}
      >
        {row.getValue("spanId") as string}
      </div>
    ),
    size: 100,
    enableResizing: true,
  },
  {
    accessorKey: "duration",
    header: "Duration",
    cell: ({ row }) => {
      const duration = row.getValue("duration") as number | undefined;
      return (
        <div className="text-xs text-muted-foreground font-mono text-right">
          {duration ? `${duration}ms` : "-"}
        </div>
      );
    },
    size: 80,
    enableResizing: true,
  },
  {
    accessorKey: "traceId",
    header: "Trace ID",
    cell: ({ row }) => (
      <div
        className="text-xs font-mono text-muted-foreground"
        title={row.getValue("traceId") as string}
      >
        {row.getValue("traceId") as string}
      </div>
    ),
    size: 100,
    enableResizing: false,
  },
];

interface TelemetryPage {
  data: TelemetrySpan[];
}

async function fetchTelemetryPage(
  agentName: string,
  offset: number,
  limit: number,
  operation?: string,
  status?: TelemetrySpanStatus,
): Promise<TelemetryPage> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    ...(operation && { operation }),
    ...(status && { status }),
  });

  const response = await fetch(`/api/agents/${agentName}/telemetry?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch telemetry: ${response.statusText}`);
  }

  const result = await response.json();
  return { data: result.data };
}

interface AgentTelemetryTableProps {
  agentName: string;
}

export function AgentLogsTable({ agentName }: AgentTelemetryTableProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState({
    status: "",
    operation: "",
    search: "",
  });
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({});

  // Simple parsing function without caching
  const getParsedAttributes = useCallback(
    (attributes: Record<string, unknown>) => {
      return parseCustomAttributes(attributes);
    },
    [],
  );

  const toggleRow = useCallback((spanId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [spanId]: !prev[spanId],
    }));
  }, []);

  // Infinite query for telemetry
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ["agent-telemetry", agentName, filters.status, filters.operation],
    queryFn: ({ pageParam = 0 }) =>
      fetchTelemetryPage(
        agentName,
        pageParam,
        50,
        filters.operation || undefined,
        (filters.status as TelemetrySpanStatus) || undefined,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.data.length === 50 ? pages.length * 50 : undefined;
    },
    staleTime: 30000, // 30 seconds
  });

  const allRows = useMemo(
    () => data?.pages?.flatMap((page) => page.data) ?? [],
    [data],
  );

  // Filter rows based on search - expanded to cover more areas
  const filteredRows = useMemo(() => {
    if (!filters.search) {
      return allRows;
    }

    const searchLower = filters.search.toLowerCase();
    return allRows.filter((row) => {
      // Basic fields
      if (
        row.operationName.toLowerCase().includes(searchLower) ||
        row.status.toLowerCase().includes(searchLower) ||
        row.spanId.toLowerCase().includes(searchLower) ||
        row.traceId.toLowerCase().includes(searchLower) ||
        row.agentName.toLowerCase().includes(searchLower)
      ) {
        return true;
      }

      // Operation details
      if (row.operationDetails) {
        const details = row.operationDetails;
        if (
          details.modelProvider?.toLowerCase().includes(searchLower) ||
          details.modelName?.toLowerCase().includes(searchLower) ||
          details.finishReason?.toLowerCase().includes(searchLower)
        ) {
          return true;
        }
      }

      // Tool calls
      if (row.toolCalls && row.toolCalls.length > 0) {
        const toolCallMatch = row.toolCalls.some((toolCall) => {
          return (
            toolCall.toolName?.toLowerCase().includes(searchLower) ||
            toolCall.errorMessage?.toLowerCase().includes(searchLower) ||
            JSON.stringify(toolCall.parameters || {})
              .toLowerCase()
              .includes(searchLower) ||
            JSON.stringify(toolCall.result || {})
              .toLowerCase()
              .includes(searchLower)
          );
        });
        if (toolCallMatch) {
          return true;
        }
      }

      // Errors
      if (row.errors && row.errors.length > 0) {
        const errorMatch = row.errors.some((error) => {
          return (
            error.errorType?.toLowerCase().includes(searchLower) ||
            error.errorMessage?.toLowerCase().includes(searchLower) ||
            error.errorStack?.toLowerCase().includes(searchLower)
          );
        });
        if (errorMatch) {
          return true;
        }
      }

      // Attributes (including all telemetry attributes)
      if (row.attributes && Object.keys(row.attributes).length > 0) {
        const attributeMatch = Object.entries(row.attributes).some(
          ([key, value]) => {
            const keyMatch = key.toLowerCase().includes(searchLower);
            const valueMatch = String(value)
              .toLowerCase()
              .includes(searchLower);
            return keyMatch || valueMatch;
          },
        );
        if (attributeMatch) {
          return true;
        }
      }

      return false;
    });
  }, [allRows, filters.search]);

  const tableColumns = useMemo(() => createColumns(), []);

  const table = useReactTable({
    data: filteredRows,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    defaultColumn: {
      size: 150,
      minSize: 50,
      maxSize: 500,
    },
  });

  const { rows } = table.getRowModel();

  // Virtualization with fixed row heights to prevent layout shifts
  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? rows.length + 1 : rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      if (index >= rows.length) {
        return 52; // Loader row
      }
      const row = rows[index];
      const isExpanded = expandedRows[row.original.spanId];
      // Fixed 400px height for expanded rows - no dynamic estimation needed
      return isExpanded ? 400 + 52 : 52; // 400px content + 52px row height
    },
    overscan: 10,
  });

  // Re-measure when expanded rows change - proper TanStack Virtual pattern
  // Use useLayoutEffect to avoid layout thrashing
  useLayoutEffect(() => {
    rowVirtualizer.measure();
  }, [rowVirtualizer]);

  // Force re-measurement when expanded rows change
  // biome-ignore lint/correctness/useExhaustiveDependencies: We need to re-measure when expanded rows change
  useLayoutEffect(() => {
    rowVirtualizer.measure();
  }, [expandedRows, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();

  // Infinite scrolling trigger
  useEffect(() => {
    const [lastItem] = [...virtualRows].reverse();

    if (
      lastItem &&
      lastItem.index >= rows.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    virtualRows,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    rows.length,
  ]);

  // Real-time telemetry updates are handled by the useEventsWebSocket hook
  // which invalidates React Query cache when telemetry.span.created events are received

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-sm text-gray-500">Loading telemetry...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-sm text-red-400">
          Error loading telemetry:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Filters */}
      <TelemetryFilters filters={filters} onFiltersChange={setFilters} />

      {/* Virtualized Table - Flex grow to fill remaining space */}
      <div
        ref={containerRef}
        className="flex-1 rounded-xl border bg-card shadow-sm overflow-hidden"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="border-b bg-muted/50">
            <div
              className="flex items-center h-12"
              style={{
                width: "100%",
                minWidth: `${table.getCenterTotalSize()}px`,
              }}
            >
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header, index) => (
                  <div
                    key={header.id}
                    className="px-3 py-2 text-sm font-medium text-muted-foreground flex items-center relative"
                    style={{
                      width: header.getSize(),
                      flexGrow:
                        index === headerGroup.headers.length - 1 ? 1 : 0,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    {header.column.getCanResize() &&
                      index !== headerGroup.headers.length - 1 && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`absolute right-0 top-0 h-full w-1 bg-border/50 hover:bg-border cursor-col-resize ${
                            header.column.getIsResizing() ? "bg-border" : ""
                          }`}
                        />
                      )}
                  </div>
                )),
              )}
            </div>
          </div>

          {/* Body */}
          <div ref={parentRef} className="flex-1 overflow-auto">
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: "100%",
                minWidth: `${table.getCenterTotalSize()}px`,
                position: "relative",
              }}
            >
              {virtualRows.map((virtualRow) => {
                const isLoaderRow = virtualRow.index > rows.length - 1;
                const row = rows[virtualRow.index];

                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    className={`absolute top-0 left-0 w-full border-b ${
                      isLoaderRow ? "flex items-center h-[52px]" : ""
                    }`}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                      height: isLoaderRow ? "52px" : "auto",
                    }}
                  >
                    {isLoaderRow ? (
                      <div className="w-full text-center py-4 text-sm text-muted-foreground">
                        {isFetchingNextPage
                          ? "Loading more..."
                          : "No more logs"}
                      </div>
                    ) : (
                      <div className="w-full">
                        <button
                          type="button"
                          className="flex items-center h-[52px] cursor-pointer hover:bg-muted/50 transition-colors text-left"
                          style={{
                            width: "100%",
                            minWidth: `${table.getCenterTotalSize()}px`,
                          }}
                          onClick={() => toggleRow(row.original.spanId)}
                          aria-expanded={expandedRows[row.original.spanId]}
                        >
                          {row.getVisibleCells().map((cell, index) => (
                            <div
                              key={cell.id}
                              className="px-3 py-2 overflow-hidden text-ellipsis flex items-center"
                              style={{
                                width: cell.column.getSize(),
                                flexGrow:
                                  index === row.getVisibleCells().length - 1
                                    ? 1
                                    : 0,
                                whiteSpace:
                                  index === row.getVisibleCells().length - 1
                                    ? "normal"
                                    : "nowrap",
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </div>
                          ))}
                        </button>
                        {expandedRows[row.original.spanId] && (
                          <div
                            className="px-3 py-4 bg-muted/30 border-t"
                            style={{
                              height: "400px",
                              width: "100%",
                              minWidth: `${table.getCenterTotalSize()}px`,
                            }}
                          >
                            <TelemetryExpandedContent
                              row={row.original}
                              getParsedAttributes={getParsedAttributes}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="text-xs text-gray-500 flex justify-between items-center shrink-0">
        <span>
          Showing {filteredRows.length} of {allRows.length} telemetry spans
        </span>
        {hasNextPage && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading..." : "Load More"}
          </Button>
        )}
      </div>
    </div>
  );
}
